import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, CheckCircle, AlertCircle, Settings, Play, Loader2, Copy, Download, Eye, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { putAwayService, CreatePutAwayRequest, CreatePutAwayResponse, PutAwayReservationDto } from '../../../../services/putAwayService';
import { itemsService } from '../../../../services/itemsService';
import { ItemsDto } from '../../../../types/items';
import { StcTransDto } from '../../../../services/stcTransService';

interface PutAwayModalProps {
  isOpen: boolean;
  onClose: () => void;
  stcTrans: StcTransDto;
  companyNo: string;
  warehouseID: string;
  onSuccess?: (reservations: PutAwayReservationDto[]) => void;
}

interface PutAwaySettings {
  binGrpNo: number;
  pickRuleNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
}

export const PutAwayModal: React.FC<PutAwayModalProps> = ({
  isOpen,
  onClose,
  stcTrans,
  companyNo,
  warehouseID,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingItemData, setLoadingItemData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatePutAwayResponse | null>(null);
  const [itemData, setItemData] = useState<ItemsDto | null>(null);
  const [settings, setSettings] = useState<PutAwaySettings>({
    binGrpNo: 1,
    pickRuleNo: 1,
    putAwayToPickableBinYesNo: true,
    quantity: stcTrans.quantity || 0
  });

  // Load item data when modal opens
  useEffect(() => {
    if (isOpen && stcTrans.itemNo) {
      loadItemData();
    }
  }, [isOpen, stcTrans.itemNo, companyNo]);

  const loadItemData = async () => {
    try {
      setLoadingItemData(true);
      setError(null);
      
      const item = await itemsService.getItemByCompanyAndItemNo(companyNo, stcTrans.itemNo);
      setItemData(item);
      
      // Update settings with item data
      setSettings(prev => ({
        ...prev,
        binGrpNo: item.binGrpNo || 1,
        pickRuleNo: item.pickRuleNo || 1,
        putAwayToPickableBinYesNo: item.putAwayToPickableBinYesNo ?? true
      }));
      
    } catch (err) {
      console.error('Error loading item data:', err);
      setError(`Kunne ikke hente varedata: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingItemData(false);
    }
  };

  const handleRunPutAway = async () => {
    if (!stcTrans.itemNo || settings.quantity <= 0) {
      setError('Varenummer og antall må være angitt');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const request: CreatePutAwayRequest = {
        companyNo,
        warehouseID,
        itemNo: stcTrans.itemNo,
        binGrpNo: settings.binGrpNo,
        pickRuleNo: settings.pickRuleNo,
        grShipmentNo: stcTrans.grShipmentNo || 0,
        putAwayToPickableBinYesNo: settings.putAwayToPickableBinYesNo,
        quantity: settings.quantity
      };

      const response = await putAwayService.createPutAwayForStcIntRelOrderLine(request);
      setResult(response);

      // Don't call onSuccess automatically as it might close the modal
      // onSuccess will be called when user explicitly closes modal with results
      // if (response.success && onSuccess) {
      //   onSuccess(response.reservations);
      // }
    } catch (err) {
      console.error('Error running put-away:', err);
      setError(`Feil ved kjøring av put-away: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Call onSuccess callback if we have successful results
    if (result?.success && onSuccess) {
      onSuccess(result.reservations);
    }
    
    // Reset state
    setResult(null);
    setError(null);
    setItemData(null);
    setLoadingItemData(false);
    setSettings({
      binGrpNo: 1,
      pickRuleNo: 1,
      putAwayToPickableBinYesNo: true,
      quantity: stcTrans.quantity || 0
    });
    
    onClose();
  };

  const handleStartOver = () => {
    setResult(null);
    setError(null);
    setItemData(null);
    setLoadingItemData(false);
    setSettings({
      binGrpNo: 1,
      pickRuleNo: 1,
      putAwayToPickableBinYesNo: true,
      quantity: stcTrans.quantity || 0
    });
    
    // Load item data again to populate settings from item
    if (stcTrans.itemNo) {
      loadItemData();
    }
  };

  const getPickRuleText = (pickRuleNo: number) => {
    switch (pickRuleNo) {
      case 1: return 'FIFO (First In, First Out)';
      case 2: return 'LIFO (Last In, First Out)';
      case 3: return 'Minste antall først';
      case 4: return 'Størst antall først';
      default: return `Regel ${pickRuleNo}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-4xl sm:max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Put-Away for StcTrans</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                Varenummer: {stcTrans.itemNo} • Antall: {stcTrans.quantity} {stcTrans.itemUnitTxt}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* StcTrans Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Transaksjonsinformasjon</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Varenummer:</span>
                <div className="font-medium">{stcTrans.itemNo}</div>
              </div>
              <div>
                <span className="text-gray-600">Beskrivelse:</span>
                <div className="font-medium">{stcTrans.description1 || 'Ikke angitt'}</div>
              </div>
              <div>
                <span className="text-gray-600">Antall:</span>
                <div className="font-medium">{stcTrans.quantity} {stcTrans.itemUnitTxt}</div>
              </div>
              <div>
                <span className="text-gray-600">Lokasjon:</span>
                <div className="font-medium">{stcTrans.locationName || 'Ikke angitt'}</div>
              </div>
              <div>
                <span className="text-gray-600">GR Shipment:</span>
                <div className="font-medium">{stcTrans.grShipmentNo || 'Ikke angitt'}</div>
              </div>
              <div>
                <span className="text-gray-600">Lager:</span>
                <div className="font-medium">{stcTrans.warehouse || warehouseID}</div>
              </div>
            </div>
          </div>

          {/* Item Data Loading */}
          {loadingItemData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                <span className="text-blue-700">Henter varedata...</span>
              </div>
            </div>
          )}

          {/* Item Data Info */}
          {itemData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900 mb-3 flex items-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span>Varedata hentet</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-green-600">Standard Bin:</span>
                  <div className="font-medium">{itemData.stdBinCode || 'Ikke angitt'}</div>
                </div>
                <div>
                  <span className="text-green-600">Bin Gruppe:</span>
                  <div className="font-medium">{itemData.binGrpNo || 'Ikke angitt'}</div>
                </div>
                <div>
                  <span className="text-green-600">Pick Regel:</span>
                  <div className="font-medium">{itemData.pickRuleNo || 'Ikke angitt'}</div>
                </div>
                <div>
                  <span className="text-green-600">Pickable Bin:</span>
                  <div className="font-medium">{itemData.putAwayToPickableBinYesNo ? 'Ja' : 'Nei'}</div>
                </div>
                <div>
                  <span className="text-green-600">Item Type:</span>
                  <div className="font-medium">{itemData.itemType || 'Standard'}</div>
                </div>
                <div>
                  <span className="text-green-600">Shared Bin:</span>
                  <div className="font-medium">{itemData.useSharedBinYesNo ? 'Ja' : 'Nei'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Put-Away Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Put-Away Innstillinger</span>
              {itemData && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                  Fra varedata
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bin Gruppe Nr
                  {itemData && itemData.binGrpNo && (
                    <span className="text-xs text-green-600 ml-1">(fra varedata)</span>
                  )}
                </label>
                <input
                  type="number"
                  value={settings.binGrpNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, binGrpNo: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pick Regel Nr
                  {itemData && itemData.pickRuleNo && (
                    <span className="text-xs text-green-600 ml-1">(fra varedata)</span>
                  )}
                </label>
                <select
                  value={settings.pickRuleNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, pickRuleNo: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={1}>1 - FIFO (First In, First Out)</option>
                  <option value={2}>2 - LIFO (Last In, First Out)</option>
                  <option value={3}>3 - Minste antall først</option>
                  <option value={4}>4 - Størst antall først</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Antall for Put-Away
                </label>
                <input
                  type="number"
                  value={settings.quantity}
                  onChange={(e) => setSettings(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max={stcTrans.quantity || 0}
                  step="0.1"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="putAwayToPickableBin"
                  checked={settings.putAwayToPickableBinYesNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, putAwayToPickableBinYesNo: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="putAwayToPickableBin" className="text-sm text-gray-700">
                  Put-away til pickable bin
                  {itemData && itemData.putAwayToPickableBinYesNo !== undefined && (
                    <span className="text-xs text-green-600 ml-1">(fra varedata)</span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {result.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">Put-Away Utført!</h3>
                      <p className="text-sm text-green-600">
                        {result.reservations.length} reservasjoner opprettet for {settings.quantity} {stcTrans.itemUnitTxt}
                      </p>
                    </div>
                  </div>
                  
                  {/* Summary Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">Totalt plassert</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.reduce((sum, r) => sum + r.resQuantity, 0)} {stcTrans.itemUnitTxt}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">Normale bins</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.filter(r => !r.dummy).length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">Pickable bins</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.filter(r => r.pickableBinYesNo && !r.dummy).length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">Dummy reservasjoner</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {result.reservations.filter(r => r.dummy).length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Reservations Table */}
                  <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                    <div className="px-4 py-3 bg-green-100 border-b border-green-200">
                      <h4 className="text-sm font-medium text-green-900 flex items-center space-x-2">
                        <MapPin className="h-4 w-4" />
                        <span>Bin Reservasjoner</span>
                        <span className="text-xs bg-green-200 px-2 py-1 rounded">
                          {result.reservations.length} reservasjoner
                        </span>
                      </h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-green-200">
                        <thead className="bg-green-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Bin Kode
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Sone
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Antall
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Pickable
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Ressurs
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-green-100">
                          {result.reservations.map((reservation, index) => (
                            <tr key={index} className={reservation.dummy ? 'bg-orange-50' : 'hover:bg-green-50'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                <div className="flex items-center space-x-2">
                                  <MapPin className={`h-4 w-4 ${reservation.dummy ? 'text-orange-500' : 'text-green-500'}`} />
                                  <span className={reservation.dummy ? 'font-mono' : ''}>{reservation.binCode}</span>
                                  {reservation.dummy && (
                                    <span className="text-xs bg-orange-100 text-orange-600 px-1 py-0.5 rounded">
                                      Dummy
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {reservation.stcZoneID || 'Ikke angitt'}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {reservation.resQuantity} {stcTrans.itemUnitTxt}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {reservation.pickableBinYesNo ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Ja
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Nei
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                #{reservation.resourceNo}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {reservation.dummy ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Manuell plassering
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Automatisk
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Dummy Reservations Warning */}
                  {result.reservations.some(r => r.dummy) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <div>
                          <h4 className="text-sm font-medium text-orange-900">Manuell plassering nødvendig</h4>
                          <p className="text-sm text-orange-700 mt-1">
                            Noen reservasjoner krever manuell plassering da det ikke finnes tilgjengelige bins med tilstrekkelig kapasitet.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <div>
                      <h3 className="text-red-800 font-medium">Put-Away Feilet</h3>
                      <p className="text-sm text-red-600 mt-2">
                        {result.errorMessage || 'Ukjent feil oppstod under put-away operasjonen'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {loadingItemData ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Henter varedata...</span>
              </div>
            ) : itemData ? (
              <span>Innstillinger hentet fra varedata</span>
            ) : (
              <span>Bruker standard innstillinger</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {result?.success && (
              <button
                onClick={handleStartOver}
                className="px-4 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
              >
                Kjør på nytt
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {result?.success ? 'Lukk' : 'Avbryt'}
            </button>
            {!result?.success && (
              <button
                onClick={handleRunPutAway}
                disabled={loading || loadingItemData || settings.quantity <= 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Kjører...</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    <span>Kjør Put-Away</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
